import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {combineLatest} from 'rxjs';
import {filter} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Router} from '@angular/router';

import {Doctor} from '@core/interfaces/doctor/doctor.interface';
import {NotificationsService} from '../../../notifications/services/notifications.service';
import {ProfileService} from '@profile/profile.service';
import {
  prepareNewNotification
} from '../../../notifications/utils/prepare-notification';
import {NotificationTypesEnum} from '../../../notifications/enums/notification-types.enum';
import {Notification} from '../../../notifications/interfaces/notification.interface';
import {NearestConsultationService} from '@core/services/nearest-consultation.service';
import {CentrifugoSocketService} from '@core/services/centrifugo-socket.service';
import {CentrifugoSocketChannelsEnum} from '@core/enums/centrifugo-socket-channels.enum';
import {logNotificationSubInfo, logSocketInfo} from '@core/utils/socket-logger';
import {MessagesCountsService} from '@core/services/messages-counts.service';

@UntilDestroy()

@Component({
  selector: 'app-new-notification-listener',
  templateUrl: './new-notification-listener.component.html',
  styleUrls: ['./new-notification-listener.component.scss']
})
export class NewNotificationListenerComponent implements OnInit, OnDestroy {
  @Output() clicked: EventEmitter<null> = new EventEmitter();

  socketClient: any;
  socket: any;

  profile: Doctor;
  notificationChannel: string;

  newNotification: Notification | null;

  constructor(
    private profileService: ProfileService,
    private centrifugoSocketService: CentrifugoSocketService,
    private notificationsService: NotificationsService,
    private nearestConsultationService: NearestConsultationService,
    private router: Router,
    private messagesCountsService: MessagesCountsService
  ) {
  }

  ngOnInit(): void {
    this.handleMainSubjects();
  }

  ngOnDestroy(): void {
    this.unsubscribeFromNotificationChannel();
    this.onCloseNewNotification();
  }

  showNewNotification(notification): void {
    this.newNotification = {...prepareNewNotification(notification)};
  }

  onCloseNewNotification(): void {
    this.newNotification = null;
  }

  private handleMainSubjects(): void {
    combineLatest([
      this.centrifugoSocketService.socketConnectionSuccess$.pipe(filter((r) => r)),
      this.profileService.profile$.pipe(filter((profile) => !!profile))
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([socket, profile]) => {
        this.socketClient = socket;
        this.profile = profile;
        this.notificationChannel = `${CentrifugoSocketChannelsEnum.NOTIFICATION}${this.profile.id}`;

        this.subscribeOnNotificationsChannel();
      });
  }

  private subscribeOnNotificationsChannel(): void {
    this.socket = this.socketClient.getSubscription(this.notificationChannel) || this.socketClient.newSubscription(this.notificationChannel);

    this.socket
      .on('subscribed', (ctx) => {
        logSocketInfo(ctx.channel);
      })
      .on('unsubscribed', (ctx) => {
        logSocketInfo(`${ctx.code}: ${ctx.reason}`);
      })
      .on('publication', (ctx) => {
        this.handleIncomingSocketMessage(ctx.data);
      })
      .subscribe();
  }

  private unsubscribeFromNotificationChannel(): void {
    this.socket.unsubscribe();
    this.socket?.removeAllListeners();
  }

  private handleIncomingSocketMessage(message): void {
    logNotificationSubInfo(message);

    if (message.type === NotificationTypesEnum.NewDiscussionMessage) {
      this.router.url.includes('/dialogues')
        ? this.notificationsService.newDiscussionNotification$.next(message)
        : this.messagesCountsService.getUnreadMessages().pipe(untilDestroyed(this)).subscribe();

      if (message?.initiator_id === this.profile.id || this.router.url.includes(`dialogues/${message.initiator_id}/${message.discussion_id}`)) {
        return;
      }
    }

    if (
      message.type === NotificationTypesEnum.CancelConsultationPatient ||
      message.type === NotificationTypesEnum.CreateConsultation
    ) {
      this.nearestConsultationService.updateNearestConsultation$.next(true);
    }

    this.onCloseNewNotification();

    setTimeout(() => {
      this.showNewNotification(message);
      this.notificationsService.notificationsUpdated$.next(true);
    }, 500);
  }
}
